/**
 * Landing-surface constants — shared between LandingPage.jsx and LandingNav.jsx
 * so the "Product ▾" dropdown always mirrors the actual Full Product Suite
 * section rendered on the landing page.
 *
 * Icons are exported as component references (NOT JSX) so both consumers can
 * render them with their own sizing/color classes.
 *
 * iter147 — Two structural changes:
 *   1. "Patriot Economist" → "Economist" everywhere user-visible.
 *   2. Decision Lab exposed as a peer product. Kept OUT of the 12-card grid
 *      but exported as `DECISION_LAB_MODULE` and slotted into the dropdown's
 *      "Analyze" group. The landing page renders it as a wide horizontal
 *      row underneath the 12 cards (col-span-3 on lg) so the grid stays
 *      visually symmetric at 3×4.
 *   3. `SUITE_GROUPS` — theme buckets used by the mega-dropdown so the nav
 *      never drifts out of sync with the marketing narrative.
 */
import {
  Rss, Layers, MessageSquareText, Sparkles, TrendingUp, Network, FolderKanban,
  LayoutDashboard, BarChart3, Bell, Share2, Plug, FlaskConical,
} from 'lucide-react';

export const SUITE_MODULES = [
  { slug: 'signals',       icon: Rss,               name: 'Signals',                blurb: 'Signal graph with rules, alerts, and version history.',                                        bullets: ['Custom rule engine', 'Change detection', 'Version history'] },
  { slug: 'stream',        icon: Layers,            name: 'Content Stream',         blurb: 'Multi-source columns · one operational reading surface.',                                      bullets: ['Source columns', 'Save · List · Bookmark', 'Deep-links to detail'] },
  // iter117 — "Ask Your Workspace" (Workspace Copilot) sits as card #3: the
  // natural "and then you can just talk to it" beat AFTER data ingest
  // (Signals + Stream) but BEFORE the structured Agents / Economist /
  // Connections cards. Framed by capability (RAG grounded in the user's
  // own data), not by underlying model, so the value lands without
  // needing to explain LLM plumbing.
  { slug: 'copilot',       icon: MessageSquareText, name: 'Ask Your Workspace',     blurb: 'Grounded AI chat over your own signals, content, files, and projects — every answer cited.',   bullets: ['RAG scoped to YOUR data', 'Cites source rows + files', 'Project-scoped Q&A'] },
  { slug: 'agents',        icon: Sparkles,          name: 'Analysis Agents',        blurb: '24 specialist agents · orchestration engine · multi-source context.',                          bullets: ['Competitor, macro, buyer', 'Orchestration recipes', 'Grounded in your data'] },
  // iter147 — "Patriot Economist" → "Economist" (public-facing rename).
  // Backend service names + internal collections stay `patriot_economist`;
  // this is a marketing display change only.
  { slug: 'economist',     icon: TrendingUp,        name: 'Economist',              blurb: 'Live 8-section macro reports, streamed as they generate.',                                     bullets: ['FRED / BLS / IMF live', 'Priority briefs', 'Explain-the-move view'] },
  { slug: 'connections',   icon: Network,           name: 'Connections Graph',      blurb: 'People · companies · topics — one live relationship map.',                                     bullets: ['CRM enrichment', 'Warm-intro paths', 'Signal-linked'] },
  { slug: 'vault',         icon: FolderKanban,      name: 'File Vault + RAG',       blurb: 'Upload once, query forever — vector-indexed private knowledge.',                               bullets: ['PDF · deck · CSV', 'Voyage embeddings', 'Chat over your files'] },
  { slug: 'command',       icon: LayoutDashboard,   name: 'Command Center',         blurb: 'One dashboard for everything that matters right now.',                                         bullets: ['KPI grid', 'Weekly AI summary', 'Custom widgets'] },
  { slug: 'analytics',     icon: BarChart3,         name: 'Analytics',              blurb: '15 endpoints · weekly AI brief · content, sentiment, credit health.',                          bullets: ['Signal health', 'Sentiment trends', 'Credit projections'] },
  { slug: 'alerts',        icon: Bell,              name: 'Notifications & Rules',  blurb: 'Custom rules · in-app, email, and webhook delivery.',                                          bullets: ['Sentiment drops', 'Keyword hits', 'Digest scheduling'] },
  { slug: 'socialops',     icon: Share2,            name: 'Social Ops',             blurb: 'LinkedIn · X · Facebook · Instagram · Threads · YouTube in one place.',                       bullets: ['OAuth-secured posts', 'AI ad drafts', 'Scheduling'] },
  // iter117 — "Universal Connectors" capstone at #12. Deliberately framed
  // as a *capability* (not a bare "Integrations" checkbox), because a
  // generic Integrations label breaks the Suite's capability-narrative
  // arc and duplicates the social-platform list already inside Social
  // Ops. Bullets name only shipped, load-bearing connectors — Zoom, Google
  // (Emergent-managed OAuth), Stripe, Voyage AI, FRED — plus the
  // developer-facing public API + outbound webhook surface prospects
  // filter on. If a connector is deprecated or hasn't shipped, do NOT
  // add it here — the Suite section is a promise about shipping product.
  { slug: 'connectors',    icon: Plug,              name: 'Universal Connectors',   blurb: 'OAuth-secured bridges to the tools intelligence teams already run — plus a public API.',       bullets: ['Zoom · Google · Stripe', 'Voyage AI · FRED macro', 'Public API + outbound webhooks'] },
];

// iter147 — Decision Lab is peer to the Suite but rendered separately on the
// landing page (wide row under the 12-card grid). Kept in its own export so
// the 3×4 grid stays symmetric.
export const DECISION_LAB_MODULE = {
  slug: 'decisionlab',
  icon: FlaskConical,
  name: 'Decision Lab',
  blurb: 'Structured intelligence analysis using the ACH methodology — build hypotheses, weigh evidence, and stress-test conclusions.',
  bullets: [
    'ACH hypothesis matrix',
    'NATO Admiralty grading',
    'Sensitivity + peer review',
  ],
};

// Full lookup — everything a nav consumer might need to render. Keyed by
// slug so `SUITE_GROUPS` can reference by slug and stay legible.
export const ALL_MODULES = [...SUITE_MODULES, DECISION_LAB_MODULE];
export const MODULE_BY_SLUG = Object.fromEntries(
  ALL_MODULES.map((m) => [m.slug, m]),
);

// iter147 — Theme groupings for the mega-dropdown ("Product ▾"). The order
// is deliberate — it mirrors the natural workflow: ingest → analyze →
// operate → extend. Do not reorder without also updating the marketing
// narrative in the Suite section.
export const SUITE_GROUPS = [
  { key: 'discover', label: 'Discover', tint: 'text-sky-300',    slugs: ['signals', 'stream', 'connections', 'vault'] },
  { key: 'analyze',  label: 'Analyze',  tint: 'text-violet-300', slugs: ['copilot', 'agents', 'economist', 'decisionlab'] },
  { key: 'operate',  label: 'Operate',  tint: 'text-emerald-300', slugs: ['command', 'analytics', 'alerts'] },
  { key: 'extend',   label: 'Extend',   tint: 'text-amber-300',   slugs: ['socialops', 'connectors'] },
];
